import { useCallback, useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/cn";
import { sendChat, type ChatMessage } from "@/lib/cosApi";

type Turn = ChatMessage & { id: string; pending?: boolean };

function newId() {
  return crypto.randomUUID();
}

export default function Chat() {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [turns]);

  const send = useCallback(async () => {
    const content = draft.trim();
    if (!content || sending) return;

    setError(null);
    const userTurn: Turn = { id: newId(), role: "user", content };
    const placeholder: Turn = {
      id: newId(),
      role: "assistant",
      content: "",
      pending: true,
    };
    const next = [...turns, userTurn, placeholder];
    setTurns(next);
    setDraft("");
    setSending(true);
    textareaRef.current?.focus();

    const history: ChatMessage[] = next
      .filter((t) => !t.pending)
      .map(({ role, content }) => ({ role, content }));

    try {
      const resp = await sendChat(history);
      setTurns((curr) =>
        curr.map((t) =>
          t.id === placeholder.id
            ? { ...t, content: resp.message.content, pending: false }
            : t,
        ),
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setTurns((curr) => curr.filter((t) => t.id !== placeholder.id));
    } finally {
      setSending(false);
    }
  }, [draft, sending, turns]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        void send();
      }
    },
    [send],
  );

  return (
    <div className="flex h-full flex-col gap-4">
      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto rounded-lg border border-border bg-card/30"
      >
        {turns.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="flex flex-col gap-3 p-4">
            {turns.map((t) => (
              <Bubble key={t.id} turn={t} />
            ))}
          </div>
        )}
      </div>
      {error && (
        <div
          role="alert"
          className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300"
        >
          {error}
        </div>
      )}
      <Composer
        draft={draft}
        sending={sending}
        onChange={setDraft}
        onKeyDown={onKeyDown}
        onSend={send}
        textareaRef={textareaRef}
      />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="grid h-full place-items-center p-8 text-center">
      <Card className="max-w-md">
        <CardContent className="space-y-2 p-6">
          <p className="text-base font-medium">Talk to CoS.</p>
          <p className="text-sm text-muted-foreground">
            Anything destructive — code edits, deploys, money — goes through a
            Telegram approval before it runs. Start with a question or a task.
          </p>
          <p className="text-xs text-muted-foreground">
            Submit with <kbd className="rounded border border-border bg-muted px-1">⌘</kbd>
            +<kbd className="rounded border border-border bg-muted px-1">↵</kbd>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function Bubble({ turn }: { turn: Turn }) {
  const isUser = turn.role === "user";
  return (
    <div className={cn("flex", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[80ch] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm",
          isUser
            ? "bg-primary text-primary-foreground"
            : "border border-border bg-card text-card-foreground",
        )}
      >
        {turn.pending ? (
          <span className="inline-flex items-center gap-2 text-muted-foreground">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current [animation-delay:120ms]" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current [animation-delay:240ms]" />
          </span>
        ) : (
          turn.content
        )}
      </div>
    </div>
  );
}

interface ComposerProps {
  draft: string;
  sending: boolean;
  onChange: (v: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
}

function Composer({ draft, sending, onChange, onKeyDown, onSend, textareaRef }: ComposerProps) {
  return (
    <Card className="bg-card/40">
      <CardContent className="p-3">
        <div className="flex items-end gap-2">
          <Textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask CoS… (Cmd/Ctrl-Enter to send)"
            className="min-h-[60px] resize-none border-0 bg-transparent shadow-none focus-visible:ring-0"
            disabled={sending}
            autoFocus
          />
          <Button
            onClick={onSend}
            disabled={sending || !draft.trim()}
            size="icon"
            aria-label="Send message"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
